import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useClerk } from '@clerk/react';
import { usePortalStore } from '@/store/portal-store.js';
import { makePortalInstance } from '@edison/api-client-react';
import { FileText, DollarSign, MessageSquare, LogOut, ShieldCheck, AlertTriangle, Sparkles, X, ChevronRight } from 'lucide-react';
import { EdisonMark } from '@/components/icons/edison-logo.js';

const TOUR_STEPS = [
  {
    Icon: Sparkles,
    label: 'Ask Edison',
    description: 'Ask plain-English questions about the claim — eligible hours, project scope, classification decisions. Edison answers from the actual claim data.',
  },
  {
    Icon: ShieldCheck,
    label: 'Eligibility',
    description: 'Per-project TFA assessment, core R&D activities, competence records, and documentation coverage — the four ATO gateway criteria under s355-20.',
  },
  {
    Icon: DollarSign,
    label: 'Expenditure',
    description: 'R&D labour hours and expenditure broken down by project and contributor. Includes non-labour costs (cloud, software licences, hardware).',
  },
  {
    Icon: MessageSquare,
    label: 'Findings',
    description: 'Raise a formal finding against any artefact, activity, or the claim itself. Findings are visible to the claim owner and tracked to resolution.',
  },
];

function ReviewerTour({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="Portal orientation">
      <div className="relative w-full max-w-lg rounded-2xl bg-gray-900 border border-gray-700 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-gray-800">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex h-6 w-6 items-center justify-center rounded-md shrink-0" style={{ background: '#0072D5' }}>
                <EdisonMark size={13} />
              </div>
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Edison Audit Portal</p>
            </div>
            <h2 className="text-lg font-semibold text-white">Welcome to the reviewer portal</h2>
            <p className="text-sm text-gray-400 mt-1">
              You have secure, read-only access to this R&D tax claim. Here's what's available.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors mt-0.5"
            aria-label="Dismiss tour"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-6 py-4 space-y-4">
          {TOUR_STEPS.map(({ Icon, label, description }) => (
            <div key={label} className="flex gap-3">
              <div className="shrink-0 mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-gray-800 border border-gray-700">
                <Icon className="h-3.5 w-3.5 text-blue-400" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{label}</p>
                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-500">
            This tour won't show again on this device.
          </p>
          <button
            onClick={onDismiss}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            Get started
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  children: React.ReactNode;
}

const NAV_ITEMS = [
  { path: '/ask', label: 'Ask Edison', Icon: Sparkles, auditorOnly: false },
  { path: '/eligibility', label: 'Eligibility', Icon: ShieldCheck, auditorOnly: false },
  { path: '/expenditure', label: 'Expenditure', Icon: DollarSign, auditorOnly: false },
  { path: '/evidence', label: 'Evidence', Icon: FileText, auditorOnly: true },
  { path: '/findings', label: 'Findings', Icon: MessageSquare, auditorOnly: false },
];

export function PortalShell({ children }: Props) {
  const token = usePortalStore((s) => s.token);
  const mode = usePortalStore((s) => s.mode);
  const clearToken = usePortalStore((s) => s.clearToken);
  const [location, navigate] = useLocation();
  const { signOut } = useClerk();
  const [accessRole, setAccessRole] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  // Claim lock status — reviewers (Group B) can view a claim that is still draft/validating and
  // the portal serves live, provisional figures. Null until the /claim fetch resolves, so the
  // banner never flashes before we actually know the claim's status.
  const [claimStatus, setClaimStatus] = useState<string | null>(null);

  // Grant-mode reviewers (Group B: authenticated external reviewers) live under
  // /reviewer/portal/*, backed by the Clerk-authenticated /api/reviewer/* routes. Anonymous
  // access-token holders (unchanged) live under /portal/*, backed by /api/portal/*.
  const basePath = mode === 'grant' ? '/reviewer/portal' : '/portal';
  const loginPath = mode === 'grant' ? '/reviewer' : '/portal/login';

  // First-login tour — shown once per device per token (localStorage key scoped to token prefix).
  // Only shown for grant-mode authenticated reviewers (not anonymous token holders).
  const tourKey = token ? `reviewer-tour-seen:${token.substring(0, 16)}` : null;
  const [showTour, setShowTour] = useState(() => {
    if (!tourKey || mode !== 'grant') return false;
    try { return !localStorage.getItem(tourKey); } catch { return false; }
  });
  const dismissTour = () => {
    if (tourKey) { try { localStorage.setItem(tourKey, '1'); } catch { /* storage unavailable */ } }
    setShowTour(false);
  };

  useEffect(() => {
    if (!token) {
      navigate(loginPath);
    }
  }, [token, navigate, loginPath]);

  useEffect(() => {
    if (!token) return;
    makePortalInstance(token, { mode })
      .get<{ data: { accessRole?: string; accessExpiresAt?: string; status?: string } }>('/api/portal/claim')
      .then((res) => {
        setAccessRole(res.data.accessRole ?? null);
        setExpiresAt(res.data.accessExpiresAt ?? null);
        setClaimStatus(res.data.status ?? null);
      })
      .catch(() => { /* the pages handle their own errors */ });
  }, [token, mode]);

  if (!token) return null;

  // Non-auditor roles don't see the raw-evidence tab on the anonymous X-Access-Token path.
  // Group B (grant-mode, authenticated) reviewers always get full auditor-depth access
  // regardless of their grant's role — the backend already serves them evidence (viaGrant in
  // auditorOnly(), audit-portal.ts) — so the nav must show the tab for them too, or the access
  // already granted server-side would simply be invisible in the UI.
  const navItems = NAV_ITEMS.filter((n) => !n.auditorOnly || accessRole === 'auditor' || mode === 'grant');

  // Days until the access window closes (for the expiry banner).
  const daysLeft = expiresAt ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000) : null;

  const handleSignOut = () => {
    clearToken();
    if (mode === 'grant') {
      void signOut(() => navigate(loginPath));
    } else {
      navigate(loginPath);
    }
  };

  return (
    <div className="dark h-screen overflow-hidden bg-gray-950 text-gray-100 flex">
      {showTour && <ReviewerTour onDismiss={dismissTour} />}
      {/* Sidebar */}
      <div className="w-56 shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col overflow-y-auto">
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-gray-800">
          <div className="flex h-7 w-7 items-center justify-center rounded-md shrink-0" style={{ background: '#0072D5' }}>
            <EdisonMark size={16} />
          </div>
          <div>
            <p className="text-sm font-semibold text-white tracking-tight">Edison</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Audit Portal</p>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1" aria-label="Portal navigation">
          {navItems.map(({ path, label, Icon }) => {
            const href = `${basePath}${path}`;
            return (
              <a
                key={href}
                href={href}
                onClick={(e) => { e.preventDefault(); navigate(href); }}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  location === href
                    ? 'bg-blue-600/20 text-blue-300 font-medium'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {label}
              </a>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-800">
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>

      {/* Main content — scrollable, fills remaining space */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {claimStatus !== null && claimStatus !== 'locked' && (
          <div className="flex items-center gap-2 bg-amber-950/60 border-b border-amber-900/50 px-8 py-2 text-sm text-amber-300 shrink-0">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Draft claim — figures are provisional and not yet locked/lodged.
          </div>
        )}
        {daysLeft !== null && daysLeft <= 7 && (
          <div className="bg-amber-950/60 border-b border-amber-900/50 px-8 py-2 text-sm text-amber-300 shrink-0">
            {daysLeft > 0
              ? `Your access expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Contact the claim owner if you need more time.`
              : 'Your access has expired. Request renewed access from the sign-in screen.'}
          </div>
        )}
        <main className="p-8 flex-1">{children}</main>
      </div>
    </div>
  );
}
